import { organisation, partners, stats } from '../data/mockData';
import { ButtonLink } from '../components/ui/Button';
import mokalengProPic from '../utils/brandImages';

const services = [
  {
    title: 'IT training',
    description:
      'MICT SETA accredited learnerships and Microsoft and CompTIA short programmes.',
  },
  {
    title: 'IT consultancy',
    description:
      'Tailor-made ICT learnership and skills programmes built around client needs.',
  },
  {
    title: 'System support',
    description:
      'Proactive maintenance, troubleshooting, and real-time support for IT infrastructure.',
  },
  {
    title: 'Cyber security',
    description:
      'Threat detection, vulnerability assessment, and real-time monitoring.',
  },
  {
    title: 'Database security',
    description:
      'Encryption, access control, and compliance for sensitive business data.',
  },
  {
    title: 'Web development',
    description:
      'Responsive websites, e-commerce platforms, and web applications.',
  },
];

export default function About() {
  return (
    <>
      {/* Intro */}
      <section className="bg-hero-glow">
        <div className="container py-16 lg:py-24">
          <div className="max-w-3xl">
            <span className="eyebrow">About {organisation.name}</span>
            <h1 className="mt-5 text-4xl leading-tight text-balance sm:text-5xl">
              Increasing IT awareness with cutting-edge technology
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-brand-900/65">
              Mokaleng IT offers advanced digital solutions and hands-on IT
              training. From expert IT services to accredited education, we
              equip businesses and learners with the tools to succeed in the
              tech world.
            </p>
          </div>

          <dl className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.id} className="card-surface p-6">
                <dt className="text-3xl font-extrabold text-brand-950">
                  {stat.value}
                </dt>
                <dd className="mt-1.5 text-sm text-brand-900/60">
                  {stat.label}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Founder */}
      <section className="bg-white">
        <div className="section-shell">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <div className="card-surface overflow-hidden">
                <img
                  src={mokalengProPic}
                  alt="Mokaleng IT"
                  className="aspect-square w-full object-cover"
                />

                <div className="p-6">
                  <h2 className="text-xl">{organisation.founder}</h2>
                  <p className="mt-1 text-sm font-semibold text-brand-600">
                    {organisation.founderTitle}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <span className="eyebrow">Who we are</span>
              <h2 className="mt-4 text-3xl sm:text-4xl">
                An IT company that trains the people it hires from
              </h2>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-brand-900/70">
                <p>
                  Mokaleng IT was founded by {organisation.founder} as an IT
                  company offering web design, networking support, IT training,
                  and consultation. The company emphasises skills development
                  through certifications such as MICT SETA and Microsoft
                  qualifications.
                </p>
                <p>
                  We provide customised IT solutions to law firms,
                  municipalities, corporate businesses, and the public, covering
                  hardware, software, server management, and ICT training. That
                  daily support work is what shapes the curriculum: learners are
                  taught the problems we actually get called out for.
                </p>
                <p>
                  Based in {organisation.location}, we have been operating for{' '}
                  {organisation.yearsExperience} years and hold MICT SETA
                  accreditation {organisation.accreditationNumber}, awarded in{' '}
                  {organisation.accreditedSince}.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accreditation */}
      <section className="bg-canvas-100">
        <div className="section-shell">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="eyebrow">Accreditation</span>
              <h2 className="mt-4 text-3xl sm:text-4xl">
                MICT SETA accredited since {organisation.accreditedSince}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-brand-900/70">
                Accreditation {organisation.accreditationNumber} signifies that
                Mokaleng IT complies with the quality standards set by SAQA and
                the QCTO for education and training. It ensures the learning
                programmes we offer are high quality, industry-relevant, and
                meet national standards, enhancing learners' qualifications and
                employability in the tech sector.
              </p>
              <ButtonLink to="/learnerships" variant="primary" size="lg" className="mt-8">
                View accredited programmes
              </ButtonLink>
            </div>

            <div className="card-surface p-8">
              <h3 className="text-sm font-bold uppercase tracking-wider text-brand-600">
                Clients and partners
              </h3>
              <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3">
                {partners.map((partner) => (
                  <li key={partner.id} className="border-b border-canvas-200 pb-2.5">
                    <span className="block text-sm font-bold text-brand-950">
                      {partner.name}
                    </span>
                    <span className="block text-xs text-brand-900/50">
                      {partner.sector}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-white">
        <div className="section-shell">
          <div className="max-w-2xl">
            <span className="eyebrow">What we do</span>
            <h2 className="mt-4 text-3xl sm:text-4xl">
              We solve IT problems with simple solutions
            </h2>
            <p className="mt-4 text-base leading-relaxed text-brand-900/65">
              Training is one part of the business. Here is the full range of
              what Mokaleng IT delivers.
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => (
              <article key={service.title} className="card-surface p-6">
                <h3 className="text-base font-bold text-brand-950">
                  {service.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-900/60">
                  {service.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
